import {
  Briefcase,
  Heart,
  Home,
  PiggyBank,
  Plane,
  Sparkles,
  Trash2,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { BottomSheet, Button, NumPad } from '../../components';
import type { Goal } from '../../data/types';
import { cn } from '../../lib/cn';
import { springQuick, tapScale } from '../../tokens';

const ICON_OPTIONS: { name: string; component: LucideIcon }[] = [
  { name: 'PiggyBank', component: PiggyBank },
  { name: 'Plane', component: Plane },
  { name: 'Home', component: Home },
  { name: 'Heart', component: Heart },
  { name: 'Briefcase', component: Briefcase },
  { name: 'Sparkles', component: Sparkles },
];

type Props = {
  /** 'add' starts a fresh goal; passing a Goal opens edit. null = closed. */
  mode: 'add' | Goal | null;
  onClose: () => void;
  onSave: (
    input:
      | {
          mode: 'add';
          goal: Omit<Goal, 'id' | 'currentAmount'> & { currentAmount?: number };
        }
      | { mode: 'edit'; id: string; patch: Partial<Goal> },
  ) => void;
  onDelete?: (id: string) => void;
};

/**
 * BottomSheet for creating or editing a savings goal. Stack: name +
 * deadline + icon picker, then NumPad for the target amount. The icon
 * picker is a 6-tile horizontal strip; selection is bg-accent/10 with
 * accent foreground.
 */
export function GoalSheet({ mode, onClose, onSave, onDelete }: Props) {
  const open = mode !== null;
  const editing = mode && mode !== 'add' ? mode : null;

  const [name, setName] = useState('');
  const [deadline, setDeadline] = useState('');
  const [iconName, setIconName] = useState('PiggyBank');
  const [target, setTarget] = useState(0);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setName(editing.name);
      setDeadline(editing.deadline ?? '');
      setIconName(editing.icon);
      setTarget(editing.targetAmount);
    } else {
      setName('');
      setDeadline('');
      setIconName('PiggyBank');
      setTarget(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing?.id]);

  const submit = (committedAmount: number) => {
    if (!name.trim() || committedAmount <= 0) return;
    if (editing) {
      onSave({
        mode: 'edit',
        id: editing.id,
        patch: {
          name: name.trim(),
          targetAmount: committedAmount,
          icon: iconName,
          deadline: deadline || undefined,
        },
      });
    } else {
      onSave({
        mode: 'add',
        goal: {
          name: name.trim(),
          targetAmount: committedAmount,
          icon: iconName,
          deadline: deadline || undefined,
        },
      });
    }
    onClose();
  };

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={editing ? 'Edit goal' : 'Add goal'}
    >
      <div className="px-card-pad pt-3 pb-1 flex flex-col gap-3">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Vacation, emergency fund…"
          autoCapitalize="sentences"
          maxLength={60}
          className="w-full h-11 px-4 rounded-button bg-surface-secondary text-[15px] text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-accent/40"
        />

        <div>
          <span className="text-caption uppercase text-text-tertiary mb-2 block">
            Icon
          </span>
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar -mx-1 px-1">
            {ICON_OPTIONS.map(({ name: optName, component: Icon }) => {
              const active = iconName === optName;
              return (
                <motion.button
                  key={optName}
                  type="button"
                  onClick={() => setIconName(optName)}
                  whileTap={tapScale}
                  transition={springQuick}
                  aria-pressed={active}
                  className={cn(
                    'shrink-0 w-12 h-12 rounded-button inline-flex items-center justify-center',
                    active
                      ? 'bg-accent text-white'
                      : 'bg-surface-secondary text-text-secondary',
                  )}
                >
                  <Icon size={20} strokeWidth={1.5} />
                </motion.button>
              );
            })}
          </div>
        </div>

        <label className="block">
          <span className="text-caption uppercase text-text-tertiary mb-2 block">
            Deadline (optional)
          </span>
          <input
            type="date"
            value={deadline}
            onChange={(e) => setDeadline(e.target.value)}
            className={cn(
              'w-full h-11 px-3 rounded-button bg-surface-secondary',
              'text-[15px] text-text-primary',
              'focus:outline-none focus:ring-2 focus:ring-accent/40',
            )}
          />
        </label>
      </div>

      <NumPad
        initialValue={target}
        onChange={setTarget}
        onDone={submit}
        doneLabel={editing ? 'Update' : 'Save'}
      />

      {editing && onDelete ? (
        <div className="px-card-pad pb-card-pad pt-1">
          <Button
            variant="destructive"
            fullWidth
            leadingIcon={<Trash2 size={16} strokeWidth={1.5} />}
            onClick={() => {
              onDelete(editing.id);
              onClose();
            }}
          >
            Delete
          </Button>
        </div>
      ) : null}
    </BottomSheet>
  );
}
